/**
 * Event Stock Allocations manager — modal/panel where the owner sets
 * qtyIn for each product allocated to an event. The cashier cannot
 * edit qtyIn (read-only), but the owner can.
 *
 * Phase 2 redesign: products are pure catalog, stock is per-event.
 * This is the ONLY place where qtyIn is set (besides event close).
 *
 * Phase 8: Allocation editor. Instead of typing the absolute new qty
 * (error-prone: typo of 550 instead of 55), the editor lets the
 * owner pick a relative change via +/- chips and shows a live
 * "New qtyIn: X" preview before saving. Same component reused for
 * the "add new product" picker in absolute mode (starts at 0).
 */

import { useEffect, useMemo, useState } from 'react';
import { Fragment } from 'react';
import { Plus, Trash2, PackagePlus, History, Package2, Pencil, AlertTriangle, Upload, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { EmptyState } from '@/components/ui/EmptyState';
import { useProducts } from '@/hooks/useProducts';
import {
  useEventAllocations,
  useEventAllocationAudit,
  useRemoveEventAllocation,
  useUpdateAllocationBatch,
} from '@/hooks/useEventAllocations';
import { useAuth } from '@/hooks/useAuth';
import { cn, formatDate, formatDateTime, formatIDR, formatNumber, isoDateToMs, msToIsoDate } from '@/lib/format';
import { usePermissions } from '@/hooks/usePermissions';
import { AllocationDeltaEditor } from './AllocationDeltaEditor';
import { AllocationImportDialog } from './AllocationImportDialog';
import { StockPickerDialog } from './StockPickerDialog';
import { groupAllocationsByProduct } from '@/lib/allocationGroups';
import type { EventStockAllocation } from '@/types/domain';

export interface EventAllocationsManagerProps {
  eventId: string | null;
}

/**
 * Stock allocations for one event.
 *
 * This used to live in a modal. It stopped fitting: the table carries product,
 * price, lot, expiry, three quantity columns and two actions, and the picker on
 * top of it needs its own search and preview. At modal width every one of those
 * was cramped, and adding a lot meant scrolling inside a box inside a box.
 * It is a page now — routed at /events/:eventId/stock, so it is also
 * linkable and survives a refresh.
 */
export function EventAllocationsManager({ eventId }: EventAllocationsManagerProps) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canEdit = can('inventory', 'allocate') && !!user;
  const { data: products } = useProducts({ isActive: true });
  const { data: allocations, isLoading } = useEventAllocations(eventId);
  const updateBatch = useUpdateAllocationBatch();
  const removeAllocation = useRemoveEventAllocation();

  // Single-editing-row model: at most one product is in edit mode.
  // Tracking a single id (rather than per-product state) is simpler
  // and matches the UX of the new delta editor.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Products to pre-tick when the picker opens from a product's "+ Lot". */
  const [pickerProductIds, setPickerProductIds] = useState<string[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'allocations' | 'history'>('allocations');

  // Reset edit state when the modal closes
  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setPickerOpen(false);
      setError(null);
      setTab('allocations');
    }
  }, [eventId]);

  // Keyed by allocation id, not product: a product can now hold several
  // batches in one event and each is edited on its own row.


  // One product, its lots underneath — instead of the product repeated per lot.
  const groups = useMemo(() => groupAllocationsByProduct(allocations ?? []), [allocations]);

  // Lots are folded under their product until asked for. A product whose lot
  // is being edited stays open, or the editor would vanish with its row.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (productId: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(productId)) n.delete(productId);
      else n.add(productId);
      return n;
    });
  const allExpanded = groups.length > 0 && groups.every((g) => expanded.has(g.productId));
  const editingProductId = (allocations ?? []).find((a) => a.id === editingId)?.productId;

  const eventIsClosed = useMemo(() => {
    return (allocations ?? []).some((a) => a.closedAt);
  }, [allocations]);

  /**
   * Save an edit to an existing allocation. Called by the
   * AllocationDeltaEditor with the final qty (already absolute, the
   * editor did the math), the delta (relative change), and the
   * reason. We pass through to setEventAllocation which uses
   * setEventAllocation (upsert). The audit log entry will read
   * "after.qtyIn = newQty" vs the previous value implicitly via the
   * before/after diff in the API.
   */
  const handleSave = async (
    a: EventStockAllocation,
    input: {
      newQty: number;
      delta: number;
      reason: string;
      lotNumber?: string;
      expiryDate?: string;
      unit?: string;
      orderForm?: string;
    },
  ) => {
    // Edits go through update_allocation_batch, which addresses the row by id.
    // setEventAllocation is an upsert keyed on the batch identity, so handing it
    // a corrected lot would insert a second row and leave the original behind.
    //
    // The owner's rule — everything editable until the batch sells, then only
    // qty — is enforced by a database trigger, so a locked field that somehow
    // reaches here is refused with a message that names the lot.
    if (!eventId || !user) return;
    setError(null);
    const hadExpiry = !!a.expiryDate;
    const wantsExpiry = !!input.expiryDate;
    try {
      await updateBatch.mutateAsync({
        allocationId: a.id,
        eventId,
        qtyIn: input.newQty,
        lotNumber: input.lotNumber,
        expiryDate: wantsExpiry ? isoDateToMs(input.expiryDate!) : undefined,
        clearExpiry: hadExpiry && !wantsExpiry,
        unit: input.unit,
        orderForm: input.orderForm,
        reason: input.reason || `Adjust by ${input.delta > 0 ? '+' : ''}${input.delta}`,
      });
      setEditingId(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleRemove = async (a: EventStockAllocation) => {
    if (!eventId || !user) return;
    setError(null);
    try {
      const reason = window.prompt(
        `Hapus alokasi untuk produk ini dari event? (alasan, opsional)`,
      );
      // If user cancelled, prompt returns null; we still proceed (no
      // reason required) unless they explicitly cancel by closing.
      if (reason === null) return;
      await removeAllocation.mutateAsync({
        eventId,
        allocationId: a.id,
        productId: a.productId,
        removedBy: user.uid,
        reason: reason || undefined,
        userDisplayName: user.displayName ?? undefined,
        userEmail: user.email ?? undefined,
      });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /**
   * Everything active, including products already on the event.
   *
   * This used to exclude anything already allocated, which was right when a
   * product could only be on an event once. With lots it is exactly backwards:
   * adding the second lot of a product means picking that product again, so
   * filtering it out would make the feature unreachable. The row shows how many
   * batches are already there instead.
   */
  const pickableProducts = useMemo(() => products ?? [], [products]);

  return (
    <div>
      {error && (
        <div className="mb-3 p-3 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
          {error}
        </div>
      )}

      {eventIsClosed && (
        <div className="mb-3 p-3 rounded border border-warning/30 bg-warning/5 text-caption text-warning">
          Event closed — allocations are read-only.
        </div>
      )}

      <div role="tablist" className="inline-flex p-1 mb-3 rounded bg-canvas border border-line">
        <TabButton
          active={tab === 'allocations'}
          onClick={() => setTab('allocations')}
          icon={<Package2 size={14} />}
          label="Allocations"
        />
        <TabButton
          active={tab === 'history'}
          onClick={() => setTab('history')}
          icon={<History size={14} />}
          label="History"
        />
      </div>

      {tab === 'history' ? (
        <HistoryView eventId={eventId} />
      ) : isLoading ? (
        <div className="py-12 text-center text-ink-subtle text-small">Loading…</div>
      ) : (allocations?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<PackagePlus size={24} strokeWidth={1.5} />}
          title="Belum ada alokasi"
          description="Tambah produk yang akan dibawa ke event ini."
          action={
            canEdit &&
            !eventIsClosed && (
              // Both entry points, not just "Add product". The import button
              // used to live only on the populated branch, so an empty event
              // — the exact moment a spreadsheet of stock is most useful —
              // had no way to import until one product was added by hand.
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  variant="secondary"
                  leftIcon={<Upload size={16} />}
                  onClick={() => setImportOpen(true)}
                >
                  Import dari Excel
                </Button>
                <Button
                  variant="primary"
                  leftIcon={<Plus size={16} />}
                  onClick={() => {
                    setPickerProductIds([]);
                    setPickerOpen(true);
                  }}
                  disabled={pickableProducts.length === 0}
                >
                  Add product
                </Button>
              </div>
            )
          }
        />
      ) : (
        <div data-tour="stock-table" className="space-y-3">
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ChevronsUpDown size={16} />}
              onClick={() => setExpanded(allExpanded ? new Set() : new Set(groups.map((g) => g.productId)))}
            >
              {allExpanded ? 'Tutup semua lot' : 'Buka semua lot'}
            </Button>
            {canEdit && !eventIsClosed && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Upload size={16} />}
                onClick={() => setImportOpen(true)}
              >
                Import dari Excel
              </Button>
            )}
            {canEdit && !eventIsClosed && (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus size={16} />}
                onClick={() => {
                  setPickerProductIds([]);
                  setPickerOpen(true);
                }}
                disabled={pickableProducts.length === 0}
              >
                Add product
              </Button>
            )}
          </div>

          <Table>
            <THead>
              <TR>
                <TH>Produk / lot</TH>
                <TH className="text-right">Harga</TH>
                <TH className="text-right">Masuk</TH>
                <TH className="text-right">Sold</TH>
                <TH className="text-right">Available</TH>
                <TH className="text-right">Aksi</TH>
              </TR>
            </THead>
            <TBody>
              {groups.map((g) => (
                <Fragment key={g.productId}>
                  {/* The product once, with the totals across its lots. Click to
                      show the lots. */}
                  <TR
                    className="bg-canvas/60 cursor-pointer hover:bg-canvas"
                    onClick={() => toggleExpanded(g.productId)}
                  >
                    <TD>
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          aria-expanded={expanded.has(g.productId)}
                          aria-label={`${expanded.has(g.productId) ? 'Tutup' : 'Buka'} lot ${g.productName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(g.productId);
                          }}
                          className="mt-0.5 w-6 h-6 -ml-1 rounded inline-flex items-center justify-center text-ink-subtle hover:text-ink hover:bg-line/40 shrink-0"
                        >
                          <ChevronRight
                            size={16}
                            className={cn('transition-transform', (expanded.has(g.productId) || editingProductId === g.productId) && 'rotate-90')}
                          />
                        </button>
                        <div className="min-w-0">
                          <p className="font-medium text-ink">{g.productName}</p>
                          <p className="text-caption text-ink-subtle">
                            <code className="font-mono">{g.productSku}</code>
                            {' · '}
                            {g.lots.length} lot
                            {!expanded.has(g.productId) && g.lots.some((l) => l.lotNumber) && (
                              <span className="ml-1">
                                ({g.lots.map((l) => l.lotNumber || '—').join(', ')})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </TD>
                    <TD className="text-right whitespace-nowrap">
                      {formatIDR(g.productPrice)}
                      <span className="text-caption text-ink-subtle"> /{g.unit}</span>
                    </TD>
                    <TD className="text-right tabular-nums font-medium">{formatNumber(g.qtyIn)}</TD>
                    <TD className="text-right tabular-nums font-medium text-ink-subtle">{formatNumber(g.qtySold)}</TD>
                    <TD className="text-right tabular-nums">
                      <Chip variant={availabilityVariant(g.available)}>{formatNumber(g.available)}</Chip>
                    </TD>
                    <TD>
                      {canEdit && !eventIsClosed && (
                        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Plus size={14} />}
                            onClick={() => {
                              setPickerProductIds([g.productId]);
                              setPickerOpen(true);
                            }}
                            title="Tambah lot lain untuk produk ini"
                          >
                            Lot
                          </Button>
                        </div>
                      )}
                    </TD>
                  </TR>

                  {(expanded.has(g.productId) || editingProductId === g.productId) && g.lots.map((a) => {
                    const available = a.qtyIn - a.qtySold;
                    const isEditing = editingId === a.id;
                    const expired = !!a.expiryDate && a.expiryDate < Date.now();
                    return (
                      <Fragment key={a.id}>
                        <TR className={cn(isEditing && 'bg-canvas/40')}>
                          <TD className="!pl-8">
                            <p className="text-small text-ink flex flex-wrap items-center gap-x-3 gap-y-0.5">
                              <span>
                                <span className="text-ink-subtle">Lot </span>
                                <span className="font-mono">{a.lotNumber || '—'}</span>
                              </span>
                              <span className={expired ? 'text-danger font-medium' : 'text-ink-subtle'}>
                                ED {a.expiryDate ? formatDate(a.expiryDate) : '—'}
                                {expired && ' · kedaluwarsa'}
                              </span>
                              {a.orderForm && (
                                <span className="text-ink-subtle">
                                  FO <span className="font-mono text-ink">{a.orderForm}</span>
                                </span>
                              )}
                            </p>
                          </TD>
                          <TD />
                          <TD className="text-right tabular-nums">{formatNumber(a.qtyIn)}</TD>
                          <TD className="text-right tabular-nums text-ink-subtle">{formatNumber(a.qtySold)}</TD>
                          <TD className="text-right tabular-nums">{formatNumber(Math.max(0, available))}</TD>
                          <TD>
                            {canEdit && !eventIsClosed && !isEditing && (
                              <div className="flex justify-end items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingId(a.id);
                                    setError(null);
                                  }}
                                  title="Ubah qty / data lot"
                                  aria-label={`Edit lot ${a.lotNumber || ''} ${a.productName}`}
                                  leftIcon={<Pencil size={14} />}
                                >
                                  Edit
                                </Button>
                                {/* Removing a lot that has sold units would erase the
                                    record of those units leaving the warehouse, and
                                    re-adding it would reset qtySold to 0 — sellable all
                                    over again. The database refuses it; the button is
                                    hidden so the refusal is never the way you find out.
                                    Lowering qty via Edit is the supported path. */}
                                {a.qtySold === 0 ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemove(a)}
                                    title="Hapus lot dari event"
                                    aria-label={`Hapus lot ${a.lotNumber || ''} ${a.productName}`}
                                    leftIcon={<Trash2 size={14} className="text-danger" />}
                                  >
                                    <span className="sr-only">Hapus</span>
                                  </Button>
                                ) : (
                                  <span
                                    className="text-caption text-ink-subtle px-2"
                                    title={`Sudah terjual ${a.qtySold} unit — lot tidak bisa dihapus. Turunkan qty lewat Edit.`}
                                  >
                                    Terkunci
                                  </span>
                                )}
                              </div>
                            )}
                          </TD>
                        </TR>
                        {isEditing && (
                          <TR className="bg-canvas/40">
                            <TD colSpan={6} className="!p-0">
                              <AllocationDeltaEditor
                                mode="delta"
                                showBatch
                                batchLocked={a.qtySold > 0}
                                batchLockedReason={
                                  a.qtySold > 0
                                    ? `Lot ini sudah terjual ${a.qtySold} unit — lot, expired date, satuan dan form order terkunci. Hanya qty yang bisa diubah.`
                                    : undefined
                                }
                                defaultLotNumber={a.lotNumber}
                                defaultExpiryDate={a.expiryDate ? msToIsoDate(a.expiryDate) : ''}
                                defaultUnit={a.unit}
                                defaultOrderForm={a.orderForm}
                                currentQty={a.qtyIn}
                                minQty={a.qtySold}
                                maxQty={9999}
                                soldQty={a.qtySold}
                                requireReason
                                productName={`${a.productName}${a.lotNumber ? ` · lot ${a.lotNumber}` : ''}`}
                                onSave={(input) => handleSave(a, input)}
                                onCancel={() => {
                                  setEditingId(null);
                                  setError(null);
                                }}
                                saving={updateBatch.isPending}
                                error={error}
                                className="!border-0 !bg-transparent rounded-none"
                              />
                            </TD>
                          </TR>
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      {importOpen && eventId && (
        <AllocationImportDialog
          open
          eventId={eventId}
          onClose={() => setImportOpen(false)}
        />
      )}

      {pickerOpen && eventId && (
        <StockPickerDialog
          eventId={eventId}
          products={pickableProducts}
          allocations={allocations ?? []}
          initialProductIds={pickerProductIds}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function availabilityVariant(n: number): 'default' | 'warning' | 'success' {
  return n <= 0 ? 'default' : n < 5 ? 'warning' : 'success';
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-small font-medium transition-colors',
        active ? 'bg-surface text-ink shadow-hover' : 'text-ink-subtle hover:text-ink',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

const ACTION_LABELS: Record<string, string> = {
  'allocation.created': 'Alokasi dibuat',
  'allocation.updated': 'Alokasi diubah',
  'allocation.removed': 'Alokasi dihapus',
  'allocation.returned_to_gudang': 'Stok dikembalikan ke gudang',
};

function HistoryView({ eventId }: { eventId: string | null }) {
  const { data: entries, isLoading, error, refetch } = useEventAllocationAudit(eventId);

  if (isLoading) {
    return <div className="py-12 text-center text-ink-subtle text-small">Loading…</div>;
  }

  // Surface query errors (typically: missing index, permission
  // denied). Without this, a failing query silently shows "no
  // history" and the user assumes their edits weren't saved.
  if (error) {
    return (
      <EmptyState
        icon={<AlertTriangle size={24} strokeWidth={1.5} />}
        title="Gagal memuat history"
        description={
          (error as Error).message ||
          'Query audit log gagal. Mungkin index belum siap atau ada masalah permission.'
        }
        action={
          <Button variant="secondary" onClick={() => refetch()}>
            Coba lagi
          </Button>
        }
      />
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <EmptyState
        icon={<History size={24} strokeWidth={1.5} />}
        title="Belum ada history"
        description="Perubahan alokasi akan muncul di sini dengan timestamp, siapa yang ubah, dan alasannya."
      />
    );
  }

  return (
    <ul className="space-y-2 max-h-[60vh] overflow-y-auto -mx-1 px-1">
      {entries.map((e) => {
        const actionLabel = ACTION_LABELS[e.action] ?? e.action;
        const isPositive = (e.after?.qtyIn ?? 0) > (e.before?.qtyIn ?? 0);
        return (
          <li
            key={e.id}
            className="p-3 rounded border border-line bg-canvas/40 flex items-start gap-3"
          >
            <span
              className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-caption font-semibold',
                e.action === 'allocation.removed'
                  ? 'bg-danger/10 text-danger'
                  : isPositive
                    ? 'bg-success/10 text-success'
                    : 'bg-warning/10 text-warning',
              )}
            >
              {e.action === 'allocation.removed' ? '−' : isPositive ? '+' : '±'}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-small font-medium text-ink">{actionLabel}</p>
                {e.productName && (
                  <span className="text-caption text-ink-subtle truncate">
                    · {e.productName}
                  </span>
                )}
              </div>
              {e.before && e.after && 'qtyIn' in e.before && 'qtyIn' in e.after && (
                <p className="text-caption text-ink-subtle mt-0.5 tabular-nums">
                  Qty: {formatNumber((e.before.qtyIn as number) ?? 0)} →{' '}
                  {formatNumber((e.after.qtyIn as number) ?? 0)}
                </p>
              )}
              {e.reason && (
                <p className="text-caption text-ink-subtle mt-0.5 italic">
                  &ldquo;{e.reason}&rdquo;
                </p>
              )}
              <p className="text-caption text-ink-subtle mt-1">
                {e.userDisplayName ?? e.userId} · {formatDateTime(e.createdAt)}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
